import { ColumnsType } from 'antd/es/table';
import { PrettyCurrency, Typography } from 'components';
import { TRANSACTION_CUSTOMIZATION_COLUMNS_PERSISTED_STATE } from 'constants/persistedStates';
import { TransactionPage } from 'gql/graphql';
import { sneakyCase } from 'helpers/sneakyCase';
import usePersistentContext from 'hooks/usePersistenceState';
import { useTranslations } from 'next-intl';
import Link from 'next/link';
import { ReactNode, useEffect, useMemo } from 'react';
import { dateTimeShort } from 'utils/time/dateTimeShort';

enum TransactionKeysEnum {
  Id = 'id',
  Title = 'title',
  Quality = 'quality',
  LanguagePair = 'languagePair',
  Amount = 'amount',
  CreatedAt = 'createdAt',
  TransactionType = 'transactionType',
  InvoiceId = 'invoiceId',
  PurchaseOrderCode = 'purchaseOrderCode'
}

export const TRANSACTION_KEYS_MAP = {
  [TransactionKeysEnum.Id]: TransactionKeysEnum.Id,
  [TransactionKeysEnum.Title]: TransactionKeysEnum.Title,
  [TransactionKeysEnum.Quality]: TransactionKeysEnum.Quality,
  [TransactionKeysEnum.LanguagePair]: sneakyCase(TransactionKeysEnum.LanguagePair),
  [TransactionKeysEnum.Amount]: TransactionKeysEnum.Amount,
  [TransactionKeysEnum.CreatedAt]: sneakyCase(TransactionKeysEnum.CreatedAt),
  [TransactionKeysEnum.TransactionType]: sneakyCase(TransactionKeysEnum.TransactionType),
  [TransactionKeysEnum.InvoiceId]: sneakyCase(TransactionKeysEnum.InvoiceId),
  [TransactionKeysEnum.PurchaseOrderCode]: sneakyCase(TransactionKeysEnum.PurchaseOrderCode)
};

export interface DataTransactionsTreeType {
  key: string;
  [TransactionKeysEnum.Id]: ReactNode;
  [TransactionKeysEnum.Title]: ReactNode;
  [TransactionKeysEnum.Quality]: ReactNode;
  [TransactionKeysEnum.LanguagePair]: ReactNode;
  [TransactionKeysEnum.CreatedAt]: ReactNode;
  [TransactionKeysEnum.TransactionType]: ReactNode;
  [TransactionKeysEnum.InvoiceId]: ReactNode;
  [TransactionKeysEnum.PurchaseOrderCode]: ReactNode;
  [TransactionKeysEnum.Amount]: ReactNode;
}

interface IUseTableTransactionsIn {
  transactions?: TransactionPage;
}

interface IUseTableTransactionsOut {
  rows?: DataTransactionsTreeType[];
  columns: ColumnsType<DataTransactionsTreeType>;
  columnsKeys: string[];
  columnsKeysFiltered: string[];
  handleOnClickBadge: (key: string) => () => void;
  TRANSACTION_KEYS_MAP: typeof TRANSACTION_KEYS_MAP;
}

export default function useTableTransactions({
  transactions
}: IUseTableTransactionsIn): IUseTableTransactionsOut {
  const t = useTranslations();

  const [customizationColumns, setCustomizationTable] = usePersistentContext(
    TRANSACTION_CUSTOMIZATION_COLUMNS_PERSISTED_STATE
  );

  const rows = transactions?.nodes?.map((ts) => {
    const languagePair =
      ts.sourceLanguageCode && ts.targetLanguageCode
        ? `${ts.sourceLanguageCode.toLocaleUpperCase()} - ${ts.targetLanguageCode.toLocaleUpperCase()}`
        : '';

    return {
      key: ts.id as string,
      [TransactionKeysEnum.Id]: (
        <Typography variant="regular4" className="select-none  text-tertiary-100">
          {ts.id}
        </Typography>
      ),
      [TransactionKeysEnum.Title]: (
        <Typography variant="regular4" className="select-none  text-tertiary-100">
          {ts.title}
        </Typography>
      ),
      [TransactionKeysEnum.Quality]: (
        <Typography variant="regular4" className="select-none  text-tertiary-100">
          {ts.quality ? String(t(`qualities.${ts.quality}` as any)) : ''}
        </Typography>
      ),
      [TransactionKeysEnum.LanguagePair]: (
        <Typography variant="regular4" className="select-none  text-tertiary-100">
          {languagePair}
        </Typography>
      ),
      [TransactionKeysEnum.CreatedAt]: (
        <Typography variant="regular4" className="select-none  text-tertiary-100">
          {dateTimeShort(ts.createdAt, { includeYear: true })}
        </Typography>
      ),
      [TransactionKeysEnum.TransactionType]: (
        <Typography variant="regular4" className="select-none  text-tertiary-100">
          {ts.transactionType}
        </Typography>
      ),
      [TransactionKeysEnum.InvoiceId]: (
        <>
          {ts.invoiceId ? (
            <Link href={`/billing/invoices/${ts.invoiceId}`} className="!font-normal">
              <Typography variant="regular4" className="select-none text-secondary-link">
                #{ts.invoiceId}
              </Typography>
            </Link>
          ) : (
            <Typography variant="regular4" className="select-none !font-normal text-tertiary-200">
              {t('billing.invoice_items.not_applicable')}
            </Typography>
          )}
        </>
      ),
      [TransactionKeysEnum.PurchaseOrderCode]: (
        <Typography variant="regular4" className="select-none  text-tertiary-100">
          {ts.purchaseOrderCode}
        </Typography>
      ),
      [TransactionKeysEnum.Amount]: <PrettyCurrency amount={ts.amount} currency={ts.currency} />
    };
  });

  const columnsBulk: ColumnsType<DataTransactionsTreeType> = useMemo(
    () => [
      {
        title: t(
          `billing.transactions.table.${TRANSACTION_KEYS_MAP[TransactionKeysEnum.Id]}` as any
        ),
        dataIndex: TransactionKeysEnum.Id,
        key: TransactionKeysEnum.Id
      },
      {
        title: t(
          `billing.transactions.table.${TRANSACTION_KEYS_MAP[TransactionKeysEnum.Title]}` as any
        ),
        dataIndex: TransactionKeysEnum.Title,
        key: TransactionKeysEnum.Title
      },
      {
        title: t(
          `billing.transactions.table.${TRANSACTION_KEYS_MAP[TransactionKeysEnum.Quality]}` as any
        ),
        dataIndex: TransactionKeysEnum.Quality,
        key: TransactionKeysEnum.Quality
      },
      {
        title: t(
          `billing.transactions.table.${
            TRANSACTION_KEYS_MAP[TransactionKeysEnum.LanguagePair]
          }` as any
        ),
        dataIndex: TransactionKeysEnum.LanguagePair,
        key: TransactionKeysEnum.LanguagePair
      },
      {
        title: t(
          `billing.transactions.table.${TRANSACTION_KEYS_MAP[TransactionKeysEnum.CreatedAt]}` as any
        ),
        dataIndex: TransactionKeysEnum.CreatedAt,
        key: TransactionKeysEnum.CreatedAt
      },
      {
        title: t(
          `billing.transactions.table.${
            TRANSACTION_KEYS_MAP[TransactionKeysEnum.TransactionType]
          }` as any
        ),
        dataIndex: TransactionKeysEnum.TransactionType,
        key: TransactionKeysEnum.TransactionType
      },
      {
        title: t(
          `billing.transactions.table.${TRANSACTION_KEYS_MAP[TransactionKeysEnum.InvoiceId]}` as any
        ),
        dataIndex: TransactionKeysEnum.InvoiceId,
        key: TransactionKeysEnum.InvoiceId,
        minWidth: 100
      },
      {
        title: t(
          `billing.transactions.table.${TRANSACTION_KEYS_MAP[TransactionKeysEnum.Amount]}` as any
        ),
        dataIndex: TransactionKeysEnum.Amount,
        key: TransactionKeysEnum.Amount,
        minWidth: 100
      },
      {
        title: t(
          `billing.transactions.table.${
            TRANSACTION_KEYS_MAP[TransactionKeysEnum.PurchaseOrderCode]
          }` as any
        ),
        dataIndex: TransactionKeysEnum.PurchaseOrderCode,
        key: TransactionKeysEnum.PurchaseOrderCode
      }
    ],
    [t]
  );

  const columns = columnsBulk.filter((column) =>
    customizationColumns ? (customizationColumns as string[]).includes(column.key as string) : true
  );

  const columnsKeys = columnsBulk.map((column) => column.key as string);

  const columnsKeysHidden = useMemo(() => [TransactionKeysEnum.Id], []);

  const columnsKeysFiltered = columnsKeys.filter((column) =>
    customizationColumns ? (customizationColumns as string[]).includes(column) : true
  );

  const handleOnClickBadge = (key: string) => () => {
    if (columnsKeysFiltered.includes(key))
      return setCustomizationTable(columnsKeysFiltered.filter((colKey) => colKey !== key));

    return setCustomizationTable([...columnsKeysFiltered, key]);
  };

  useEffect(() => {
    setTimeout(() => {
      if (!customizationColumns || !(customizationColumns as any)?.length) {
        const columnsKeysFiltered = columnsBulk
          .map((column) => column.key as TransactionKeysEnum)
          .filter((key) => !columnsKeysHidden.includes(key));

        setCustomizationTable(columnsKeysFiltered);
      }
    }, 3000);
  }, [columns, columnsBulk, columnsKeysHidden, customizationColumns, setCustomizationTable]);

  return {
    rows,
    columns,
    columnsKeys,
    columnsKeysFiltered,
    handleOnClickBadge,
    TRANSACTION_KEYS_MAP
  };
}
